/**
 * Modal para editar estado de una cuenta.
 * Solo visible para PSP_ADMIN (RULE-29).
 *
 * @taskId T476
 * @see functional/Specs/specs_cycle_4.md HU-27-FE §Pantalla 4
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@flexibilitypay/ui';

import { useUpdateAccountStatus } from '../hooks/use-accounts';
import type { AccountStatus, AccountSummary } from '../types/account.types';
import { getValidTransitions } from '../types/account.types';

export interface AccountEditStatusModalProps {
  account: AccountSummary | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const STATUS_LABELS: Record<AccountStatus, string> = {
  ACTIVE: 'Activa',
  SUSPENDED: 'Suspendida',
  BLOCKED: 'Bloqueada',
  DEACTIVATED: 'Desactivada',
  CLOSED: 'Cerrada',
};

export function AccountEditStatusModal({
  account,
  open,
  onClose,
  onSuccess,
}: AccountEditStatusModalProps) {
  const formKey = account?.id ?? 'empty';

  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        {open ? (
          <AccountEditStatusFormBody
            key={formKey}
            account={account}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface AccountEditStatusFormBodyProps {
  account: AccountSummary;
  onClose: () => void;
  onSuccess?: () => void;
}

function AccountEditStatusFormBody({
  account,
  onClose,
  onSuccess,
}: AccountEditStatusFormBodyProps) {
  const { t } = useTranslation(['accounts', 'common']);

  // Estado local del formulario
  const [nuevoEstado, setNuevoEstado] = useState<AccountStatus | ''>('');
  const [motivo, setMotivo] = useState('');
  const [formError, setFormError] = useState('');

  // Mutation
  const mutation = useUpdateAccountStatus();

  // Transiciones válidas
  const validTransitions = account ? getValidTransitions(account.estado) : [];

  // Submit handler
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setFormError('');

      // Validaciones
      if (!nuevoEstado) {
        setFormError(t('accounts:editStatus.errors.selectStatus', 'Seleccione un nuevo estado'));
        return;
      }
      if (!motivo.trim()) {
        setFormError(t('accounts:editStatus.errors.motivoRequired', 'El motivo es obligatorio'));
        return;
      }
      if (motivo.trim().length < 10) {
        setFormError(t('accounts:editStatus.errors.motivoTooShort', 'El motivo debe tener al menos 10 caracteres'));
        return;
      }

      try {
        await mutation.mutateAsync({
          id: account.id,
          payload: {
            nuevoEstado: nuevoEstado as AccountStatus,
            motivo: motivo.trim(),
          },
        });
        onSuccess?.();
        onClose();
      } catch {
        setFormError(t('accounts:editStatus.errors.updateFailed', 'Error al actualizar el estado'));
      }
    },
    [account, nuevoEstado, motivo, mutation, onClose, onSuccess, t],
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('accounts:editStatus.title', 'Cambiar Estado de Cuenta')}</DialogTitle>
        <DialogDescription>
          <span className="font-mono text-xs">{account.cvu}</span>
          {' - '}
          <span>{account.merchantRazonSocial}</span>
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
          {/* Estado actual */}
          <div className="space-y-2">
            <Label>{t('accounts:editStatus.currentStatus', 'Estado actual')}</Label>
            <div className="p-2 bg-muted rounded-md text-sm font-medium">
              {STATUS_LABELS[account.estado]}
            </div>
          </div>

          {/* Nuevo estado */}
          <div className="space-y-2">
            <Label htmlFor="nuevo-estado">
              {t('accounts:editStatus.newStatus', 'Nuevo estado')}
            </Label>
            {validTransitions.length === 0 ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t(
                    'accounts:editStatus.noValidTransitions',
                    'No hay transiciones válidas desde el estado actual.',
                  )}
                </AlertDescription>
              </Alert>
            ) : (
              <Select
                value={nuevoEstado}
                onValueChange={(value) => setNuevoEstado(value as AccountStatus)}
              >
                <SelectTrigger id="nuevo-estado">
                  <SelectValue placeholder={t('accounts:editStatus.selectPlaceholder', 'Seleccione estado...')} />
                </SelectTrigger>
                <SelectContent>
                  {validTransitions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Motivo */}
          <div className="space-y-2">
            <Label htmlFor="motivo">
              {t('accounts:editStatus.motivo', 'Motivo del cambio')}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Textarea
              id="motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={t('accounts:editStatus.motivoPlaceholder', 'Describa el motivo del cambio de estado...')}
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {motivo.length}/500 {t('accounts:editStatus.characters', 'caracteres')}
            </p>
          </div>

          {/* Error */}
          {(formError || mutation.error) && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {formError || mutation.error?.message}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:cancel', 'Cancelar')}
            </Button>
            <Button
              type="submit"
              disabled={validTransitions.length === 0 || mutation.isPending}
            >
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common:save', 'Guardar')}
            </Button>
          </DialogFooter>
      </form>
    </>
  );
}
